<script setup lang="ts">
// #795: 匿名プレイヤーが作品から戻るときの、サインアップ導線。全露出面から同じものを
// 1 つだけ開くため、状態は usePlaySignupCta（useState）で共有し、本体は app.vue に
// 1 度だけマウントする（#751 の SignInActionDialog と同じ形）。
//
// chrome は #751 に揃える（羊皮紙 Dialog は使わず、平叙の Vuetify ダイアログ）。
// コピーは #795 で確定。冒険/創作といった特定路線には紐付けず、サインアップ自体を目的に
// する。順序は frontend_ui_patterns.md の「CTA を最初に」。
import { mdiGamepadVariantOutline } from '@mdi/js'
import { setPostLoginRedirect } from '~/composables/auth/postLoginRedirect'
import { PUBLIC_GALLERY_PATH } from '~/composables/usePlaySignupCta'

const { isOpen, dismiss, accept, browse } = usePlaySignupCta()
const route = useRoute()

// サインアップ後は遊んでいた作品に戻す（#548 と同じ sessionStorage 機構）。
// 離脱は保留してあるので、この時点の route はまだ /works/<slug>。
// ⚠ ここは WorkPlay 側の「CTA を出したら離脱を保留する」形に依存している（#821 で
// route guard 経由の離脱も同じ保留に載せた）。保留をやめて遷移を通す形に変えるなら、
// リダイレクト先は CTA を開いた時点で捕まえておくこと。
const goToAuth = (path: '/signup' | '/signin') => {
  setPostLoginRedirect(route.fullPath)
  accept()
  navigateTo(path)
}

/**
 * #844: 一次ボタンを「ほかの作品を見る」に降ろした。
 *
 * 遊びに来た人にいきなり「登録」は**この面で一番高い要求**で、実際 prod 30 日で
 * clicked は 0 だった。まず「もう 1 本遊ぶ」という安い要求に置き換え、登録は
 * 二次に残す（回遊が増えれば、登録を聞く機会そのものが増える）。
 *
 * ⚠ 保留中の離脱は再開しない。ギャラリーへ遷移すると `WorkPlay` の guard が
 * 改めて走り、`PUBLIC_GALLERY_PATH` の免除で素通りする。
 */
const goToGallery = () => {
  browse()
  navigateTo(PUBLIC_GALLERY_PATH)
}

const onModelValue = (value: boolean) => {
  if (!value) dismiss()
}
</script>

<template>
  <v-dialog
    :model-value="isOpen"
    max-width="380"
    aria-label="ほかの作品を遊ぶ・登録する"
    @update:model-value="onModelValue"
  >
    <v-card class="play-signup-cta-card pa-2">
      <div class="text-center pt-4">
        <v-icon
          :icon="mdiGamepadVariantOutline"
          color="#90caf9"
          size="40"
        />
      </div>

      <v-card-title class="play-signup-cta-title text-center text-wrap">
        ほかの作品も遊んでみませんか？
      </v-card-title>

      <v-card-text class="play-signup-cta-sub text-center text-medium-emphasis pb-2">
        登録しなくても遊べます。登録すると、お気に入りや自分の作品を残せます。
      </v-card-text>

      <v-card-actions class="play-signup-cta-actions flex-column px-4 pb-4">
        <v-btn
          color="primary"
          variant="flat"
          block
          size="large"
          @click="goToGallery"
        >
          ほかの作品を見る
        </v-btn>
        <v-btn
          variant="outlined"
          block
          class="mt-2"
          @click="goToAuth('/signup')"
        >
          無料で登録する
        </v-btn>
        <v-btn
          variant="text"
          block
          size="small"
          class="mt-1 text-medium-emphasis"
          @click="goToAuth('/signin')"
        >
          アカウントをお持ちの方
        </v-btn>
        <v-btn
          variant="text"
          block
          size="small"
          class="mt-1 text-medium-emphasis"
          @click="dismiss"
        >
          今はしない
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.play-signup-cta-title {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.4;
}

.play-signup-cta-sub {
  font-size: 0.95rem;
}

.play-signup-cta-actions :deep(.v-btn + .v-btn) {
  margin-inline-start: 0;
}
</style>
